import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Trophy, MagnifyingGlass, GameController, Users, ChartLineUp,
  Plus, Crown, CalendarDots, Star
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeaguesPage() {
  const [leagues, setLeagues] = useState([]);
  const [myLeagues, setMyLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('browse');
  const [gameFilter, setGameFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const params = gameFilter ? `?game=${gameFilter}` : '';
        const [browseRes, myRes] = await Promise.all([
          axios.get(`${API}/api/leagues${params}`, { withCredentials: true }),
          axios.get(`${API}/api/leagues/my`, { withCredentials: true }),
        ]);
        setLeagues(browseRes.data);
        setMyLeagues(myRes.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [gameFilter]);

  const TYPE_COLORS = {
    casual: '#10B981', competitive: '#EF4444', ladder: '#F59E0B', team: '#3B82F6', shop_league: '#9D4CDD',
  };

  const LeagueCard = ({ lg }) => (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4 hover:border-[#F59E0B]/30 transition-colors cursor-pointer"
      onClick={() => navigate(`/leagues/${lg.id}`)}
      data-testid={`league-card-${lg.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
            <Trophy size={18} weight="duotone" className="text-[#F59E0B]" />
          </div>
          <div>
            <h3 className="text-[14px] font-semibold text-white">{lg.name}</h3>
            <div className="flex items-center gap-1.5 text-[11px] text-[#555]">
              <GameController size={11} /> {lg.game_id}
            </div>
          </div>
        </div>
        <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-medium"
          style={{ color: TYPE_COLORS[lg.league_type] || '#888', borderColor: `${TYPE_COLORS[lg.league_type] || '#888'}30`, background: `${TYPE_COLORS[lg.league_type] || '#888'}08` }}>
          {lg.league_type?.replace(/_/g, ' ')}
        </span>
      </div>
      <p className="text-[12px] text-[#666] mb-3 line-clamp-2">{lg.description}</p>
      <div className="flex items-center gap-3 text-[11px] text-[#555]">
        <span className="flex items-center gap-1"><Users size={12} /> {lg.member_count} members</span>
        <span className="flex items-center gap-1"><ChartLineUp size={12} /> {lg.scoring_system_type}</span>
      </div>
    </motion.div>
  );

  return (
    <div className="max-w-4xl mx-auto" data-testid="leagues-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Leagues</h1>
          <p className="text-[13px] text-[#444] mt-0.5">Compete in organized leagues with seasons and standings</p>
        </div>
        <Button size="sm" className="h-8 text-[11px] bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold"
          onClick={() => navigate('/leagues/create')} data-testid="create-league-btn">
          <Plus size={13} className="mr-1" /> New League
        </Button>
      </div>

      <div className="flex gap-1 mb-4 bg-[#0c0c14] rounded-lg p-1 w-fit border border-white/[0.06]">
        {[{ id: 'browse', label: 'Browse' }, { id: 'my', label: 'My Leagues' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${tab === t.id ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'text-[#555] hover:text-[#999]'}`}
            data-testid={`tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'browse' && (
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-xs">
            <GameController size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333]" />
            <input value={gameFilter} onChange={e => setGameFilter(e.target.value)} placeholder="Filter by game..."
              className="w-full bg-[#0c0c14] border border-white/[0.06] rounded-md pl-9 pr-3 py-2 text-[13px] text-white focus:border-[#F59E0B]/30 focus:outline-none placeholder-[#2a2a2a]"
              data-testid="filter-game" />
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-white/[0.03] rounded-lg animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(tab === 'browse' ? leagues : myLeagues).map(lg => <LeagueCard key={lg.id} lg={lg} />)}
          {(tab === 'browse' ? leagues : myLeagues).length === 0 && (
            <div className="col-span-2 text-center py-12 text-[#444]">
              <Trophy size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-[13px]">{tab === 'browse' ? 'No leagues found' : 'You haven\'t joined any leagues yet'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
